import React, { useEffect, useState } from 'react';
import {
  View, Text, ScrollView, StyleSheet, ActivityIndicator,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RunStackParamList } from '../../navigation/AppNavigator';
import { runsApi } from '../../api';
import type { Run, RunSegment } from '../../types';
import { paceMinsPerMile } from '../../utils/geo';

type Props = NativeStackScreenProps<RunStackParamList, 'RunDetail'>;

function fmtPace(pace: number): string {
  const m = Math.floor(pace);
  const s = Math.round((pace - m) * 60);
  return `${m}:${String(s).padStart(2, '0')}/mi`;
}

function fmtDur(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.round(secs % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** Build ordered list of landmark names from segments, with cumulative stats at each stop. */
function buildLandmarkStops(segments: RunSegment[]): Array<{
  name: string;
  cumDistMiles: number;
  cumDurSecs: number;
  isStart: boolean;
}> {
  if (segments.length === 0) return [];
  const stops: Array<{ name: string; cumDistMiles: number; cumDurSecs: number; isStart: boolean }> = [];
  // First landmark (start of first segment)
  stops.push({ name: segments[0].fromLandmarkName, cumDistMiles: 0, cumDurSecs: 0, isStart: true });
  let cumDist = 0;
  let cumDur = 0;
  for (const seg of segments) {
    cumDist += seg.distanceMiles;
    cumDur += seg.durationSecs;
    stops.push({ name: seg.toLandmarkName, cumDistMiles: cumDist, cumDurSecs: cumDur, isStart: false });
  }
  return stops;
}

export default function RunDetailScreen({ route }: Props) {
  const { runId } = route.params;
  const [run, setRun] = useState<Run | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    runsApi.get(runId).then(setRun).finally(() => setLoading(false));
  }, [runId]);

  if (loading) return <ActivityIndicator style={{ flex: 1 }} size="large" color="#1a73e8" />;
  if (!run) return <View style={styles.container}><Text>Run not found.</Text></View>;

  const pace = run.totalDurationSecs > 0 && run.totalDistanceMiles > 0
    ? paceMinsPerMile(run.totalDistanceMiles, run.totalDurationSecs)
    : 0;

  return (
    <ScrollView style={styles.container}>
      {/* Run summary */}
      <View style={styles.summary}>
        <Text style={styles.summaryDate}>{new Date(run.startedAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</Text>
        <Text style={styles.summaryMode}>{run.mode === 'free' ? 'Free Run' : 'Preset Run'}</Text>
        <View style={styles.summaryStats}>
          <View style={styles.stat}>
            <Text style={styles.statVal}>{run.totalDistanceMiles.toFixed(2)}</Text>
            <Text style={styles.statLbl}>Miles</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statVal}>{fmtDur(run.totalDurationSecs)}</Text>
            <Text style={styles.statLbl}>Time</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statVal}>{pace > 0 ? fmtPace(pace) : '—'}</Text>
            <Text style={styles.statLbl}>Pace</Text>
          </View>
        </View>
      </View>

      {/* Encountered Landmarks */}
      {run.segments.length > 0 && (() => {
        const stops = buildLandmarkStops(run.segments);
        return (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Landmarks Visited</Text>
            {stops.map((stop, idx) => (
              <View key={idx} style={styles.stopRow}>
                <View style={styles.stopPin}>
                  <View style={[styles.pinDot, stop.isStart && styles.pinDotStart]} />
                  {idx < stops.length - 1 && <View style={styles.pinLine} />}
                </View>
                <View style={styles.stopInfo}>
                  <Text style={styles.stopName}>{stop.name}</Text>
                  {stop.isStart
                    ? <Text style={styles.stopMeta}>Start</Text>
                    : <Text style={styles.stopMeta}>{fmtDur(stop.cumDurSecs)} · {stop.cumDistMiles.toFixed(2)} mi</Text>
                  }
                </View>
              </View>
            ))}
          </View>
        );
      })()}

      {/* Segment Performances */}
      {run.segments.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Segment Performances</Text>
          {run.segments.map((seg, idx) => (
            <View key={idx} style={styles.segRow}>
              <View style={styles.segLabels}>
                <Text style={styles.segRoute}>{seg.fromLandmarkName} → {seg.toLandmarkName}</Text>
                <Text style={styles.segDist}>{seg.distanceMiles.toFixed(2)} mi</Text>
              </View>
              <View style={styles.segStats}>
                <Text style={styles.segTime}>{fmtDur(seg.durationSecs)}</Text>
                <Text style={styles.segPace}>{fmtPace(seg.paceMinsPerMile)}</Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  summary: { backgroundColor: '#1a73e8', padding: 24 },
  summaryDate: { color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 2 },
  summaryMode: { color: '#fff', fontWeight: '700', fontSize: 18, marginBottom: 16 },
  summaryStats: { flexDirection: 'row', justifyContent: 'space-around' },
  stat: { alignItems: 'center' },
  statVal: { color: '#fff', fontSize: 24, fontWeight: '700' },
  statLbl: { color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 },
  section: { backgroundColor: '#fff', margin: 16, borderRadius: 12, overflow: 'hidden' },
  sectionTitle: { fontWeight: '700', fontSize: 15, padding: 16, borderBottomWidth: 1, borderColor: '#f0f0f0' },
  segRow: {
    flexDirection: 'row', padding: 14,
    borderBottomWidth: 1, borderColor: '#f8f9fa',
    justifyContent: 'space-between',
  },
  segLabels: { flex: 1 },
  segRoute: { fontWeight: '600', fontSize: 14 },
  segDist: { color: '#888', fontSize: 12, marginTop: 2 },
  segStats: { alignItems: 'flex-end' },
  segTime: { fontWeight: '700', fontSize: 15, color: '#1a73e8' },
  segPace: { color: '#555', fontSize: 12, marginTop: 2 },
  // Landmark stop list
  stopRow: { flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 0 },
  stopPin: { width: 24, alignItems: 'center', marginRight: 12 },
  pinDot: {
    width: 12, height: 12, borderRadius: 6,
    backgroundColor: '#1a73e8', marginTop: 14,
  },
  pinDotStart: { backgroundColor: '#34a853' },
  pinLine: { width: 2, flex: 1, backgroundColor: '#d0d0d0', minHeight: 12 },
  stopInfo: { flex: 1, paddingVertical: 12, borderBottomWidth: 1, borderColor: '#f0f0f0' },
  stopName: { fontWeight: '600', fontSize: 14 },
  stopMeta: { color: '#888', fontSize: 12, marginTop: 2 },
});
